/*
 * The keyboard model BeTable and BeIconView share: roving tabindex, arrows,
 * page keys and type-ahead. Movement geometry comes from react-aria's
 * ListKeyboardDelegate, which finds items by `data-key` and derives grid rows
 * from measured rects -- so a reflowing `auto-fill` grid needs no row wrappers.
 */

import type { KeyboardEvent, Ref, RefObject } from "react";
import { useCallback, useRef, useState } from "react";
import { ListKeyboardDelegate } from "react-aria";

const TYPEAHEAD_RESET_MS = 600;
const SEARCH_COLLATOR = new Intl.Collator(undefined, { usage: "search", sensitivity: "base" });
const MOVE_KEYS = new Set(["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft", "Home", "End", "PageDown", "PageUp"]);

export type BeListLayout = "stack" | "grid";

type Keyed = { id: string };

/* react-aria's `Key`, spelled out: an isolated install exposes @react-types/shared
 * to react-aria, not to us. The shapes are still checked at the constructor. */
type BeKey = string | number;

function node<T extends Keyed>(item: T, index: number, text: string) {
	return {
		type: "item",
		key: item.id,
		value: item,
		level: 0,
		hasChildNodes: false,
		childNodes: [],
		rendered: null,
		textValue: text,
		index,
	};
}

/* Built per keystroke, not memoised: it is a view over `items`, so nothing can go stale. */
function collectionOf<T extends Keyed>(items: T[], textOf: (item: T) => string | null) {
	const nodes = items.map((item, i) => node(item, i, textOf(item) ?? ""));
	const byId = new Map(items.map((item, i) => [item.id, i]));
	const indexOf = (key: BeKey) => byId.get(String(key)) ?? -1;
	return {
		size: nodes.length,
		[Symbol.iterator]: () => nodes[Symbol.iterator](),
		getKeys: () => items.map((i) => i.id),
		getItem: (key: BeKey) => nodes[indexOf(key)] ?? null,
		at: (i: number) => nodes[i] ?? null,
		getKeyBefore: (key: BeKey) => items[indexOf(key) - 1]?.id ?? null,
		getKeyAfter: (key: BeKey) => items[indexOf(key) + 1]?.id ?? null,
		getFirstKey: () => items[0]?.id ?? null,
		getLastKey: () => items[items.length - 1]?.id ?? null,
	};
}

/* The delegate's own page keys measure `ref` and bail to first/last unless IT
 * scrolls (ListKeyboardDelegate: `if (menu && !isScrollable(menu))`). Ours never
 * does -- the BeScrollView above it holds the overflow -- so paging is stepped
 * here against that ancestor, and only the row step is the delegate's. */
function pageHeight(el: HTMLElement | null): number {
	for (let p = el; p; p = p.parentElement) {
		const oy = getComputedStyle(p).overflowY;
		if ((oy === "auto" || oy === "scroll") && p.scrollHeight > p.clientHeight) return p.clientHeight;
	}
	return window.innerHeight;
}

/* `direction` is load-bearing: getKeyLeftOf is `getNextColumn(key, direction === "ltr")`,
 * so leaving it unset sends BOTH horizontal arrows forward. */
export function beListDelegate<T extends Keyed>(
	items: T[],
	textOf: (item: T) => string | null,
	ref: RefObject<HTMLElement | null>,
	layout: BeListLayout,
) {
	return new ListKeyboardDelegate<T>({
		collection: collectionOf(items, textOf),
		ref,
		layout,
		collator: SEARCH_COLLATOR,
		direction: "ltr",
	});
}

export type BeListKeyboardOptions<T> = {
	layout: BeListLayout;
	/** The run type-ahead matches. Null for an item whose label is not a string. */
	textOf: (item: T) => string | null;
	/** Called for the item moved to; both views select on move. */
	onMove?: (id: string, item: T) => void;
	/** The root ref from `be()`. Items are found by `data-key` beneath it. */
	ref: Ref<never>;
};

export function useBeListKeyboard<T extends Keyed>(items: T[], selected: Set<string>, options: BeListKeyboardOptions<T>) {
	const containerRef = useRef<HTMLElement | null>(null);
	const [activeId, setActiveId] = useState<string | null>(null);
	const typeahead = useRef({ buf: "", at: 0 });

	const rovingId =
		(activeId != null && items.some((i) => i.id === activeId) && activeId) ||
		items.find((i) => selected.has(i.id))?.id ||
		items[0]?.id ||
		null;

	function itemEl(key: BeKey) {
		return containerRef.current?.querySelector<HTMLElement>(`[data-key="${CSS.escape(String(key))}"]`) ?? null;
	}

	/** One viewport of `getKeyBelow`/`getKeyAbove` steps, so a grid pages by rows. */
	function pageKey(d: ListKeyboardDelegate<T>, from: string, down: boolean): BeKey | null {
		const start = itemEl(from)?.getBoundingClientRect();
		if (!start) return down ? d.getLastKey() : d.getFirstKey();
		const page = pageHeight(containerRef.current);
		let key: BeKey = from;
		for (;;) {
			const next: BeKey | null = down ? d.getKeyBelow(key) : d.getKeyAbove(key);
			const rect = next == null ? null : itemEl(next)?.getBoundingClientRect();
			if (next == null || !rect) return key;
			key = next;
			if (Math.abs(rect.top - start.top) >= page) return key;
		}
	}

	function moveTo(key: BeKey | null | undefined) {
		if (key == null) return;
		const id = String(key);
		const item = items.find((i) => i.id === id);
		if (!item) return;
		setActiveId(id);
		itemEl(id)?.focus();
		options.onMove?.(id, item);
	}

	const forwarded = options.ref;
	const setContainer = useCallback(
		(el: HTMLElement | null) => {
			containerRef.current = el;
			if (typeof forwarded === "function") forwarded(el as never);
			else if (forwarded) (forwarded as { current: unknown }).current = el;
		},
		[forwarded],
	);

	/* Call AFTER the component's own keys, so activation and tree latches win. A
	 * `stack` delegate drops getKeyLeftOf/RightOf, which is how BeTable keeps
	 * Left/Right for its latches while BeIconView gets them from the grid. */
	function handleKey(e: KeyboardEvent<HTMLElement>, id: string) {
		const typed = e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey;
		if (!typed && !MOVE_KEYS.has(e.key)) return;

		const d = beListDelegate(items, options.textOf, containerRef, options.layout);

		const to = ((): BeKey | null | undefined => {
			switch (e.key) {
				case "ArrowDown":
					return d.getKeyBelow(id);
				case "ArrowUp":
					return d.getKeyAbove(id);
				case "ArrowRight":
					return d.getKeyRightOf?.(id);
				case "ArrowLeft":
					return d.getKeyLeftOf?.(id);
				case "Home":
					return d.getFirstKey();
				case "End":
					return d.getLastKey();
				case "PageDown":
					return pageKey(d, id, true);
				case "PageUp":
					return pageKey(d, id, false);
				default:
					return undefined;
			}
		})();

		if (to !== undefined) {
			e.preventDefault();
			moveTo(to);
			return;
		}

		if (e.key.length === 1 && !e.altKey && !e.ctrlKey && !e.metaKey) {
			const t = typeahead.current;
			const now = Date.now();
			t.buf = now - t.at > TYPEAHEAD_RESET_MS ? e.key : t.buf + e.key;
			t.at = now;

			const from = t.buf.length === 1 ? d.getNextKey(id) : id;
			const hit = (from != null ? d.getKeyForSearch(t.buf, from) : null) ?? d.getKeyForSearch(t.buf);
			if (hit != null) {
				e.preventDefault();
				moveTo(hit);
			}
		}
	}

	return { rovingId, setActiveId, setContainer, handleKey, moveTo };
}
